import * as React from "react";
import {observer} from "mobx-react-lite";
import {ActionIcon, Center, Group, Menu, SimpleGrid, Stack, Text} from "@mantine/core";
import {IconChevronLeft, IconDownload, IconExclamationCircle} from "@tabler/icons-react";

import {requestExport} from "../../../services/exportService";
import {DrawerState} from "../../../viewmodel/appLayout";
import {useAppLayout} from "../../../hooks/useAppLayout";
import {useQueryResponseViewModel} from "../../../hooks/useQueryResponseViewModel";
import {useEnableExperimentalFeatures} from "../../../hooks/useEnableExperimentalFeatures";
import {NeuronTable} from "./NeuronTable";
import {errorNotification, infoNotification} from "../../common/NotificationHelper";
import {ExportFormat, ReconstructionSpace} from "../../../models/neuron";

export const NeuronListContainerWidth = "470px";

const exportLimit = 25;

export const NeuronListContainer = observer(() => {
    const appLayout = useAppLayout();

    const queryResponse = useQueryResponseViewModel();

    const enableExperimentalFeatures = useEnableExperimentalFeatures();

    async function exportNeurons(ids: string[], format: ExportFormat) {
        if (ids.length === 0) {
            return;
        }

        infoNotification("Export Requested", "A download will begin when the data is ready.");

        if (!await requestExport(ids, format, ReconstructionSpace.Atlas)) {
            errorNotification("Export Failed", "Additional details are not available.");
        }
    }

    const neurons = queryResponse.neuronViewModels.filter(v => v.isSelected);

    const ids = neurons.map(n => n.ReconstructionId);

    let exportMenu = (
        <Menu disabled={ids.length == 0}>
            <Menu.Target>
                <IconDownload size={22}/>
            </Menu.Target>
            <Menu.Dropdown>
                <Menu.Item onClick={() => exportNeurons(ids, ExportFormat.SWC)}>
                    Export SWC
                </Menu.Item>
                {enableExperimentalFeatures &&
                    <Menu.Item onClick={() => exportNeurons(ids, ExportFormat.Parquet)}>
                        Export Parquet
                    </Menu.Item>}
            </Menu.Dropdown>
        </Menu>
    );

    if (ids.length > exportLimit) {
        exportMenu = (
            <Menu>
                <Menu.Target>
                    <IconDownload size={22}/>
                </Menu.Target>
                <Menu.Dropdown>
                    <Menu.Item>
                        <Group gap={2}><IconExclamationCircle size={12} color="red"/>{`Export is limited to ${exportLimit} selected neurons at one time`}
                        </Group>
                    </Menu.Item>
                </Menu.Dropdown>
            </Menu>
        );
    }

    const renderedHeader = (
        <SimpleGrid p={8} cols={3} bg="section" style={{height: "40px"}}>
            {exportMenu}
            <Text ta="center">Neurons</Text>
            <Group justify="end">
                <ActionIcon variant="subtle" onClick={() => appLayout.setNeuronDrawerState(DrawerState.Hidden)}>
                    <IconChevronLeft size={22}/>
                </ActionIcon>
            </Group>
        </SimpleGrid>
    );

    let content = <Center p={12}><Text size="sm" c="dimmed">Perform a search to view matching neurons</Text></Center>;

    if (queryResponse.queryTime >= 0) {
        content = queryResponse.matchCount == 0 ?
            <Center p={12}><Text size="sm" c="dimmed">There were no neurons that met the specified criteria.</Text></Center> : <NeuronTable/>;
    }

    return (
        <Stack gap={0} w={NeuronListContainerWidth}>
            {renderedHeader}
            <div style={{order: 1, flexGrow: 1, overflow: "auto"}}>
                {content}
            </div>
        </Stack>
    );
});
